import React, { useState } from 'react';
import { Search, X, ChevronLeft, ChevronRight, MessageCircle } from 'lucide-react';
import { mockGallery } from '../mock';

const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Extended gallery with more images
  const galleryImages = [
    ...mockGallery,
    {
      id: 5,
      title: "Natpa",
      image: "pics/gallery/natpa1.jpeg"
    },
    {
      id: 6,
      title: "Navita",
      image: "pics/gallery/navita1.jpg"
    },
    {
      id: 7,
      title: "Nithyam",
      image: "pics/gallery/nithyam1.webp"
    },
    {
      id: 8,
      title: "Nivasam",
      image: "pics/gallery/nivasam1.jpg"
    },
    {
      id: 9,
      title: "Natpa",
      image: "pics/natpa/natpa2.webp"
    },
    {
      id: 10,
      title: "Navita",
      image: "pics/navita/navita3.webp"
    },
    {
      id: 11,
      title: "Nandika",
      image: "pics/nandika/nandika3.webp"
    },
    {
      id: 12,
      title: "Nakshatra",
      image: "pics/nakshatra/nakshatra3.webp"
    },
    {
      id: 13,
      title: "Narmad",
      image: "pics/narmad/narmad3.webp"
    },
  ];

  const filteredImages = galleryImages;

  const handleImageClick = (image, index) => {
    setSelectedImage(image);
    setCurrentImageIndex(index);
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    const newIndex = (currentImageIndex + 1) % filteredImages.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentImageIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentImageIndex(newIndex);
    setSelectedImage(filteredImages[newIndex]);
  };

  const handleWhatsAppClick = () => {
    const message = selectedImage 
      ? `Hello, I am interested in your ${selectedImage.title} project. Please provide more details.`
      : 'Hello, I would like to know more about your projects shown in the gallery.';
    window.open(`https://wa.me/919884611368?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-yellow-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl lg:text-6xl font-bold text-yellow-600 mb-6">
              Project Gallery
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Explore our portfolio of completed projects showcasing architectural excellence, 
              premium interiors, and world-class amenities across Chennai.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredImages.map((image, index) => (
              <div
                key={image.id}
                onClick={() => handleImageClick(image, index)}
                className="group cursor-pointer bg-white rounded-2xl shadow-lg border-2 border-gray-100 hover:border-yellow-600 transition-all duration-300 overflow-hidden transform hover:scale-105 hover:shadow-xl"
              >
                <div className="aspect-video bg-gray-200 overflow-hidden relative">
                  <img
                    src={image.image}
                    alt={image.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 right-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 opacity-0 group-hover:opacity-100">
                    <h3 className="text-white font-bold text-lg mb-1">{image.title}</h3>
                    <span className="text-yellow-300 text-sm font-medium bg-yellow-600 bg-opacity-80 px-2 py-1 rounded-full">
                      {image.category}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredImages.length === 0 && (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="h-12 w-12 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">No Images Found</h3>
              <p className="text-gray-600 max-w-md mx-auto">
                Try selecting a different category to view our project gallery.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Image Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50" onClick={handleCloseModal}>
          <div className="relative max-w-6xl w-full max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/50 to-transparent p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-2">{selectedImage.title}</h2>
                  <span className="bg-yellow-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                    {selectedImage.category}
                  </span>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-white hover:text-yellow-400 p-2 rounded-full hover:bg-black/20 transition-colors duration-200"
                >
                  <X className="h-8 w-8" />
                </button>
              </div>
            </div>

            {/* Main Image */}
            <div className="relative bg-black rounded-2xl overflow-hidden">
              <img
                src={selectedImage.image}
                alt={selectedImage.title}
                className="w-full h-auto max-h-[70vh] object-contain mx-auto"
              />
              
              {/* Navigation Arrows */}
              {filteredImages.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-200"
                  >
                    <ChevronLeft className="h-8 w-8" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all duration-200"
                  >
                    <ChevronRight className="h-8 w-8" />
                  </button>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/50 to-transparent p-6">
              <div className="flex justify-between items-center">
                <div className="text-white">
                  <span className="text-sm opacity-75">
                    {currentImageIndex + 1} of {filteredImages.length}
                  </span>
                </div>
                <button
                  onClick={handleWhatsAppClick}
                  className="inline-flex items-center px-6 py-3 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105"
                >
                  <MessageCircle className="mr-2 h-5 w-5" />
                  Inquire About This Project
                </button>
              </div>
            </div>

            {/* Image Counter Dots */}
            {filteredImages.length > 1 && (
              <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {filteredImages.slice(Math.max(0, currentImageIndex - 2), currentImageIndex + 3).map((_, index) => {
                  const actualIndex = Math.max(0, currentImageIndex - 2) + index;
                  return (
                    <button
                      key={actualIndex}
                      onClick={() => {
                        setCurrentImageIndex(actualIndex);
                        setSelectedImage(filteredImages[actualIndex]);
                      }}
                      className={`w-3 h-3 rounded-full transition-all duration-200 ${
                        actualIndex === currentImageIndex ? 'bg-yellow-600 scale-125' : 'bg-white/50 hover:bg-white/75'
                      }`}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-yellow-600 to-yellow-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Inspired by Our Work?
          </h2>
          <p className="text-xl text-yellow-100 mb-8 max-w-2xl mx-auto">
            Let's create something extraordinary together. From concept to completion, 
            we'll bring your vision to life with the same excellence you see in our gallery.
          </p>
          <button
            onClick={handleWhatsAppClick}
            className="inline-flex items-center px-8 py-4 bg-white text-yellow-600 font-bold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            <MessageCircle className="mr-2 h-6 w-6" />
            Start Your Dream Project
          </button>
        </div>
      </section>
    </div>
  );
};

export default GalleryPage;